import { ref, toRef, computed, watch } from 'vue'
import { api } from '@/helpers/api'
import { UPGRADE_REASONS } from '@/constants/subscriptions'
import useNotification from '@/composables/useNotification.js'
import { useVenuePlans } from '@/composables/features/subscriptions/useVenuePlans'
import { usePaymentMethods } from '@/composables/features/payments-methods/usePayments'
import { useBillingCycles } from '@/composables/features/subscriptions/useBillingCycles'

const URLS = {
    xhr: {
        fetch: {
            userSubscription: '/api/profile/stripe/user/get-plan'
        },
        mutate: {
            subscribe: '/api/profile/subscriptions/subscribe'
        }
    }
}

const load = ref(false)
const userSubscription = ref({})

export function useSubscription(props, { emit }) {
    const role = toRef(props, 'role')
    const selectedPlanName = toRef(props, 'selectedPlanName')
    const { notification } = useNotification()
    const { venuePlans } = useVenuePlans()
    const {
        subscriptionIntervals,
        selectedCycle,
        selectBillingCycle,
        monthPrice,
        monthTotalPrice,
        yearPrice,
        yearTotalPrice
    } = useBillingCycles()
    const { favPayment } = usePaymentMethods()

    const plans = computed(() => {
        switch (role.value) {
            case 'venue':
                return venuePlans.value
            default:
                return null
        }
    })
    const selectedPlan = computed(() => plans.value.find((plan) => plan.selected))
    const canTrial = computed(() => {
        switch (role.value) {
            case 'venue': {
                return !userSubscription.value.user_had_trial_period
            }
            default:
                return false
        }
    })

    ;(async () => {
        if (!['venue', 'artist'].includes(role.value)) {
            throw new Error('Incorrect role.value passed into useSubscription composable.')
        }

        if (!Object.keys(userSubscription.value).length) await _fetchUserPlan()
    })()

    watch([() => venuePlans.value, () => selectedPlanName.value], ([plans, selectedPlanName]) => {
        if (plans.length && selectedPlanName) {
            selectPlan(selectedPlanName)
        }
    })

    function selectPlan(name) {
        const selectedPlan = plans.value.find((plan) => plan.name === name || plan.internalName === name)

        if (selectedPlan && selectedPlan.selectable) {
            plans.value.forEach((plan) => (plan.selected = false))
            selectedPlan.selected = true
        }
    }
    async function subscribe() {
        const data = _prepSubscribeData()
        try {
            load.value = true

            await api({
                method: 'post',
                url: URLS.xhr.mutate.subscribe,
                data
            })

            notification('success', 'Subscribed Successfully.')
            emit('done')
        } finally {
            load.value = false
        }
    }

    async function _fetchUserPlan() {
        try {
            load.value = true
            const data = await api({
                method: 'get',
                url: `${URLS.xhr.fetch.userSubscription}/${role.value}`
            })

            userSubscription.value = data

            _disableCurrentPlan(userSubscription.value.subscription.plan_name)
        } finally {
            load.value = false
        }
    }

    function _disableCurrentPlan(name) {
        const currentPlan = plans.value.find((plan) => plan.internalName === name)

        if (currentPlan) {
            currentPlan.selectable = false
        }
    }

    function _prepSubscribeData() {
        const plan = { ...selectedPlan.value }
        plan.id = selectedCycle.value.interval === 'month' ? plan.planIds.month : plan.planIds.year
        plan.name = plan.internalName
        plan.trial_days = canTrial.value ? 30 : 1
        plan.product = selectedCycle.value.interval === 'month' ? plan.planProducts.month : plan.planProducts.year
        plan.interval = selectedCycle.value.interval
        plan.price = selectedCycle.value.interval === 'month' ? plan.planPrices.month : plan.planPrices.year

        return {
            plan: plan,
            paymentMethod: favPayment.value.card_stripe_id,
            name: 'venue'
        }
    }

    return {
        UPGRADE_REASONS,
        load,
        canTrial,
        plans,
        selectedPlan,
        userSubscription,
        subscriptionIntervals,
        selectedCycle,
        monthPrice,
        monthTotalPrice,
        yearPrice,
        yearTotalPrice,
        selectBillingCycle,
        selectPlan,
        subscribe
    }
}
